/****************************************************
 * Module Name:  userColumnPermissionMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import * as React from "react";
import SwipeableViews from "react-swipeable-views";
import { useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import Constants from "../../utility/constants";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import { TiGroupOutline } from "react-icons/ti";
import NoPermission from "../common/noPermission";
import VirtualTeamUserColumnPermission from "./virtualTeamUserColumnPermission";
import SingleUserColumnPermission from "./singleUserColumnPermission";
import MultipleUsersColumnPermission from "./multipleUserColumnPermission";
import SingleColumnPermission from "./singleColumnPermission";
import MultipleColumnPermissions from "./multipleColumnPermission";
import PageTitle from "../common/pageTitle";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 1 }}>
          <Typography component={"span"}>{children}</Typography>
        </Box>
      )}
    </Box>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
    value: index,
  };
}

export default function UserColumnPermissionMaster() {
  React.useEffect(() => {
    PageTitle("PERMISSION_USERCOLUMNPERMISSION");
  }, []);

  const theme = useTheme();
  const [value, setValue] = React.useState(
    Constants.USER_PERMISSION.find(
      (iPermission) =>
        iPermission.permissionid ===
        Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_USER
    ) !== undefined
      ? 0
      : Constants.USER_PERMISSION.find(
        (iPermission) =>
          iPermission.permissionid ===
          Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_USERS
      ) !== undefined
        ? 1
        : Constants.USER_PERMISSION.find(
          (iPermission) =>
            iPermission.permissionid ===
            Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_VIRTUALTEAM
        ) !== undefined
          ? 2
          : Constants.USER_PERMISSION.find(
            (iPermission) =>
              iPermission.permissionid ===
              Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_PERMISSION
          ) !== undefined
            ? 3
            : Constants.USER_PERMISSION.find(
              (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_PERMISSIONS
            )
              ? 4
              : 5
  );

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleChangeIndex = (index) => {
    setValue(index);
  };

  return value === 5 ? (
    <NoPermission />
  ) : (
    <Box sx={{ bgcolor: "background.paper", width: "100%", padding: "10px" }}>
      <AppBar position="static" sx={{ bgcolor: "#03a9f4", height: 50 }}>
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="secondary"
          textColor="inherit"
          variant="fullWidth"
          aria-label="full width tabs example"
          sx={{
            "& .css-1vw5i9y-MuiButtonBase-root-MuiTab-root": {
              color: "#212121",
              fontWeight: "bold",
              textAlign: "center",
            },
            "& .MuiTab-root": {
              paddingBottom: 4,
            },
            "& .Mui-selected": {
              bgcolor: "#0277bd !important",
              color: "#fafafa !important",
              fontWeight: "bold !important",
              paddingBottom: 4
            }
          }}
          TabIndicatorProps={{ style: { background: "#0277bd" } }}
        >
          {Constants.USER_PERMISSION.find(
            (iPermission) =>
              iPermission.permissionid ===
              Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_USER
          ) !== undefined ? (
            <Tab
              icon={<PersonRoundedIcon />}
              iconPosition="start"
              label="User"
              {...a11yProps(0)}
            />
          ) : null}
          {Constants.USER_PERMISSION.find(
            (iPermission) =>
              iPermission.permissionid ===
              Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_USERS
          ) !== undefined ? (
            <Tab
              icon={<GroupRoundedIcon />}
              iconPosition="start"
              label="Users"
              {...a11yProps(1)}
            />
          ) : null}
          {Constants.USER_PERMISSION.find(
            (iPermission) =>
              iPermission.permissionid ===
              Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_VIRTUALTEAM
          ) !== undefined ? (
            <Tab
              icon={<TiGroupOutline size={"1.6rem"} />}
              iconPosition="start"
              label="Virtual Team"
              {...a11yProps(2)}
            />
          ) : null}
          {Constants.USER_PERMISSION.find(
            (iPermission) =>
              iPermission.permissionid ===
              Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_PERMISSION
          ) !== undefined ? (
            <Tab
              icon={<TiGroupOutline size={"1.6rem"} />}
              iconPosition="start"
              label="Permission"
              {...a11yProps(3)}
            />
          ) : null}
          {Constants.USER_PERMISSION.find(
            (iPermission) =>
              iPermission.permissionid ===
              Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_PERMISSIONS
          ) !== undefined ? (
            <Tab
              icon={<TiGroupOutline size={"1.6rem"} />}
              iconPosition="start"
              label="Permissions"
              {...a11yProps(4)}
            />
          ) : null}
        </Tabs>
      </AppBar>
      <SwipeableViews
        axis={theme.direction === "rtl" ? "x-reverse" : "x"}
        index={value}
        onChangeIndex={handleChangeIndex}
      >
        {Constants.USER_PERMISSION.find(
          (iPermission) =>
            iPermission.permissionid ===
            Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_USER
        ) !== undefined ? (
          <TabPanel value={value} index={0}>
            <SingleUserColumnPermission />
          </TabPanel>
        ) : null}
        {Constants.USER_PERMISSION.find(
          (iPermission) =>
            iPermission.permissionid ===
            Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_USERS
        ) !== undefined ? (
          <TabPanel value={value} index={1}>
            <MultipleUsersColumnPermission />
          </TabPanel>
        ) : null}
        {Constants.USER_PERMISSION.find(
          (iPermission) =>
            iPermission.permissionid ===
            Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_VIRTUALTEAM
        ) !== undefined ? (
          <TabPanel value={value} index={2}>
            <VirtualTeamUserColumnPermission />
          </TabPanel>
        ) : null}
        {Constants.USER_PERMISSION.find(
          (iPermission) =>
            iPermission.permissionid ===
            Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_PERMISSION
        ) !== undefined ? (
          <TabPanel value={value} index={3}>
            <SingleColumnPermission />
          </TabPanel>
        ) : null}
        {Constants.USER_PERMISSION.find(
          (iPermission) =>
            iPermission.permissionid ===
            Constants.PERMISSIONS.PERMISSION_USERCOLUMNPERMISSION_PERMISSIONS
        ) !== undefined ? (
          <TabPanel value={value} index={4}>
            <MultipleColumnPermissions />
          </TabPanel>
        ) : null}
      </SwipeableViews>
    </Box>
  );
}
